import { INCOGNITO_MASK } from "../../../Components/Private"
import { isIncognitoActive } from "../../../Utils/incognito"
import { buildComparisonColumnsHtml } from "../../DashboardPage/components/chartTooltips"

// The platform's dark chart tooltip (DAA-2-25), as the merchant Deflect trends draw it: a #27272A
// card with the interval's date as a muted heading, "Total: N" under it for a stacked chart (as the
// Alerts Dashboard's), a row per series with its marker, name and value, and the comparison rows
// muted under a hairline, as on the Alerts overview. It sits above the hovered point — 16px for a
// multi-line chart, 20px for a single series — and below it when there is no room above, and it
// glides from point to point.
// `rowsFor(dataIndex, chart)` returns { header, total, rows, comparisonRows }; a row is
// { marker, label, value } and a marker { color, dashed }. Or it returns { columns, stacked }, a column
// per period ({ header, total }), drawn and placed as the platform's comparison card (prevent/analytics,
// Alerts by Descriptor). `tooltipId` keeps each chart's element its own; the chart removes it.

const ROW_STYLE =
  "display:flex;align-items:center;justify-content:space-between;gap:24px;padding-top:8px"

// Labels and headers can carry merchant or channel names: never markup.
const escapeHtml = (text) =>
  String(text ?? "").replace(
    /[&<>"']/g,
    (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]
  )

const markerHtml = ({ color, dashed }) =>
  dashed
    ? `<span style="display:inline-block;width:14px;border-top:1.5px dashed ${color};margin-right:8px"></span>`
    : `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:8px"></span>`

const valueHtml = (value) =>
  `<span style="font-weight:500">${isIncognitoActive() ? INCOGNITO_MASK : escapeHtml(value)}</span>`

const rowHtml = (row) =>
  `<div style="${ROW_STYLE}"><span style="display:flex;align-items:center">` +
  `${row.marker ? markerHtml(row.marker) : ""}${escapeHtml(row.label)}</span>${valueHtml(row.value)}</div>`

const rowsHtml = ({ header, total, rows, comparisonRows = [] }) => {
  const totalHtml = total
    ? `<div style="font-weight:500;padding-top:8px">${escapeHtml(total.label)}: ${valueHtml(total.value)}</div>`
    : ""
  const comparisonHtml = comparisonRows.length
    ? `<div style="margin-top:8px;border-top:1px solid #3F3F46;color:#A1A1AA">${comparisonRows.map(rowHtml).join("")}</div>`
    : ""

  return (
    `<div style="font-weight:500;color:#7A7A83">${escapeHtml(header)}</div>` +
    totalHtml +
    rows.map(rowHtml).join("") +
    comparisonHtml
  )
}

// The platform's "current period | comparison period" card: side by side with a hairline between,
// or one under the other on a phone. Its builder maps every column's rows, so each gets an empty list.
const columnsHtml = ({ columns, stacked }) =>
  buildComparisonColumnsHtml(
    columns.map((column) => ({
      header: escapeHtml(column.header),
      total: `${escapeHtml(column.total.label)}: ${valueHtml(column.total.value)}`,
      rows: []
    })),
    { stacked }
  )

const tooltipElement = (tooltipId) => {
  const existing = document.getElementById(tooltipId)

  if (existing) return existing

  const element = document.createElement("div")
  element.id = tooltipId
  document.body.appendChild(element)

  return element
}

// Above the hovered point, as every platform chart tooltip, and below it when there is no room.
const placeAtPoint = (chart, element, offset) => {
  const canvas = chart.canvas.getBoundingClientRect()
  const pointX = canvas.left + chart.tooltip.caretX
  const pointY = canvas.top + chart.tooltip.caretY
  const width = element.offsetWidth
  const height = element.offsetHeight
  const left = Math.min(Math.max(8, pointX - width / 2), window.innerWidth - width - 8)
  const top = pointY - height - offset < 8 ? pointY + offset : pointY - height - offset

  element.style.left = `${left}px`
  element.style.top = `${top}px`
}

// As the platform's comparison card: 20px above the current period's point — not the caret, which
// two lines put between their points — and kept over the plot near its edges, 85% / 15% of the card
// to the point's left at the right / left edge.
const placeAsComparisonCard = (chart, element, dataIndex) => {
  const canvas = chart.canvas.getBoundingClientRect()
  const point = chart.getDatasetMeta(0).data[dataIndex]
  const width = element.offsetWidth
  const height = element.offsetHeight
  let shift = width / 2

  if (chart.width - point.x <= width / 2) {
    shift = width * 0.85
  } else if (point.x < width / 2) {
    shift = width * 0.15
  }

  const left = Math.min(Math.max(8, canvas.left + point.x - shift), window.innerWidth - width - 8)
  const top = Math.max(8, canvas.top + point.y - height - 20)

  element.style.left = `${left}px`
  element.style.top = `${top}px`
}

export const externalTooltip =
  ({ tooltipId, rowsFor, offset = 16 }) =>
  (context) => {
    const element = tooltipElement(tooltipId)
    const { chart, tooltip: model } = context

    if (model.opacity === 0 || !model.dataPoints?.length) {
      element.style.opacity = 0
      return
    }

    const dataIndex = model.dataPoints[0].dataIndex
    const content = rowsFor(dataIndex, chart)

    element.innerHTML = content.columns ? columnsHtml(content) : rowsHtml(content)

    // Glide between points while already visible; fade in place on first show, as the platform's.
    const wasVisible = element.style.opacity === "1"

    Object.assign(element.style, {
      opacity: 1,
      position: "fixed",
      zIndex: "1010",
      background: "#27272A",
      borderRadius: "12px",
      padding: "12px",
      color: "#FFFFFF",
      fontSize: "14px",
      fontFamily: "Inter, sans-serif",
      // The comparison card is the platform's 280px wide.
      minWidth: content.columns ? "280px" : "180px",
      pointerEvents: "none",
      transition: wasVisible
        ? "left 0.13s cubic-bezier(0.22, 1, 0.36, 1), top 0.13s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.12s ease"
        : "opacity 0.12s ease"
    })

    if (content.columns) {
      placeAsComparisonCard(chart, element, dataIndex)
    } else {
      placeAtPoint(chart, element, offset)
    }
  }
